import { useMutation } from '@apollo/client';
import { ErrorMessage } from '@hookform/error-message';
import GoogleMapsAutocompleteField from '@website/components/forms/fields/GoogleMapsAutocompleteField';
import { SubmitButton } from '@website/components/forms/fields/SubmitButton';
import {
  UpsertWayPointInput,
  WayPointType,
  WaypointUpsertDocument,
  EditTripWaypointsQuery,
} from '@website/types/graphqlOperations';
import { DateTime } from 'luxon';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';

/**
 * Create the default values
 */
export function composeWaypointEditFormDefaultValues(
  itineraryItems: EditTripWaypointsQuery['trip']['itinerary']['items'],
  tripID: string,
  waypoint?: EditTripWaypointsQuery['trip']['wayPoints']['items'][0],
): Partial<UpsertWayPointInput> {
  const arriveAt = waypoint?.arriveAt?.localTime
    ? DateTime.fromISO(waypoint.arriveAt.localTime).toFormat('HH:mm')
    : undefined;

  const defaults = {
    // Defaults
    itineraryItemID: itineraryItems[0].id,
    tripID,
    type: WayPointType.MeetPoint,
    // Waypoint settings, if set
    ...waypoint,
    // Arrive at (override zone)
    arriveAt,
  };

  // Remove Apollo typename
  // eslint-disable-next-line no-underscore-dangle
  delete (defaults as any).__typename;

  return defaults;
}

export function DayOptions({
  itineraryItems,
}: {
  itineraryItems: EditTripWaypointsQuery['trip']['itinerary']['items'];
}) {
  return (
    <>
      {itineraryItems.map((item, key) => (
        <option key={item.id} value={item.id}>
          Day {key + 1}
        </option>
      ))}
    </>
  );
}

/**
 * Form for editing waypoints
 */
export default function WaypointEditForm({
  waypoint,
  itineraryItems,
  trip,
  onSuccess = () => {},
}: {
  waypoint?: EditTripWaypointsQuery['trip']['wayPoints']['items'][0];
  itineraryItems: EditTripWaypointsQuery['trip']['itinerary']['items'];
  trip: Pick<EditTripWaypointsQuery['trip'], 'id' | 'startTimezone'>;
  onSuccess?: () => void;
}) {
  const [upsertWaypoint, { loading }] = useMutation(WaypointUpsertDocument, {
    update(cache, { data }) {
      // only update cache, if item is new, not existing one
      if (waypoint?.id) {
        return;
      }
      const tripCacheID = cache.identify({
        __typename: 'Trip',
        id: trip.id,
      });

      const waypointCacheID = cache.identify({
        __typename: 'WayPoint',
        id: data.upsertWayPoint.id,
      });

      cache.modify({
        id: tripCacheID,
        fields: {
          wayPoints(cachedWaypoints) {
            return {
              items: [...cachedWaypoints.items, { __ref: waypointCacheID }],
            };
          },
        },
      });
    },
  });

  const defaultValues = composeWaypointEditFormDefaultValues(
    itineraryItems,
    trip.id,
    waypoint,
  );

  const {
    control,
    handleSubmit,
    register,
    clearErrors,
    setError,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<UpsertWayPointInput>({
    mode: 'onBlur',
    defaultValues,
  });

  const submitForm = async (fd: UpsertWayPointInput) => {
    try {
      await upsertWaypoint({
        variables: {
          input: fd,
        },
      });
      // do not clear form, if it's updating an existing WayPoint
      if (!waypoint) {
        reset();
      }
      onSuccess();
    } catch (e) {
      if (e.networkError) {
        alert(
          'Internet connection lost - please retry when you are connected.',
        );
      } else {
        alert(e);
      }
    }
  };

  return (
    <>
      <Form onSubmit={handleSubmit(submitForm)}>
        <Form.Group controlId="type">
          <Form.Label>Type *</Form.Label>

          <Form.Select
            name="type"
            {...register('type', {
              required: true,
            })}
            className="w-auto"
          >
            <option value={WayPointType.MeetPoint} key={WayPointType.MeetPoint}>
              Meet point
            </option>

            <option
              value={WayPointType.DepartPoint}
              key={WayPointType.DepartPoint}
            >
              Departure point
            </option>
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="itineraryItemID">
          <Form.Label>Day *</Form.Label>

          <Form.Select
            name="itineraryItemID"
            {...register('itineraryItemID', {
              required: true,
            })}
            className="w-auto"
          >
            <DayOptions itineraryItems={itineraryItems} />
          </Form.Select>
        </Form.Group>

        <Form.Group controlId="arriveAt">
          <Form.Label>Time *</Form.Label>
          <Form.Control
            name="arriveAt"
            type="time"
            {...register('arriveAt', {
              required: true,
              setValueAs: (value) => {
                // convert datetime to AWSTime format
                return `${value}:00.000`;
              },
            })}
            className="w-auto"
          />
          <ErrorMessage
            errors={errors}
            name="arriveAt"
            render={({ message }) => (
              <Form.Text as="p" className="text-danger">
                {message ?? 'Arrive At is required!'}
              </Form.Text>
            )}
          />
        </Form.Group>

        <Form.Group controlId="address">
          <Form.Label>Address *</Form.Label>
          <GoogleMapsAutocompleteField
            name="address"
            control={control}
            setError={setError}
            clearErrors={clearErrors}
            required
          />
          <ErrorMessage
            errors={errors}
            name="address"
            render={({ message }) => (
              <Form.Text as="p" className="text-danger">
                {message ?? 'Address is required!'}
              </Form.Text>
            )}
          />
        </Form.Group>
        <Form.Group>
          <SubmitButton isSubmitting={loading || isSubmitting} />
        </Form.Group>
      </Form>
    </>
  );
}
